/**
 * Module - Branding Multi-Domaine
 *
 * @description
 * Gère l'état global du branding (logo, couleurs, textes, mode Light/Full)
 * selon le domaine d'accès de l'application.
 *
 * @author iLabs
 * @date 2024
 */

import { getBrandingByHostname, defaultBrandingKey } from '@/config/brandingConfig'

export const branding = {
  namespaced: true,

  state: {
    branding: defaultBrandingKey,
    isLightMode: false
  },

  getters: {
    /**
     * Récupère la configuration de branding complète
     * @param {object} state - État du module
     * @returns {object} Configuration branding
     */
    branding: (state) => state.branding,

    /**
     * Récupère le mode Light/Full
     * @param {object} state - État du module
     * @returns {boolean} true si mode Light, false si mode Full
     */
    isLightMode: (state) => state.isLightMode,

    /**
     * Récupère le chemin du logo
     * @param {object} state - État du module
     * @returns {string} Chemin du logo
     */
    logoPath: (state) => state.branding.logoPath,

    /**
     * Récupère la couleur primaire
     * @param {object} state - État du module
     * @returns {string} Code couleur hexadécimal
     */
    primaryColor: (state) => state.branding.primaryColor,

    /**
     * Récupère le nom de la marque
     * @param {object} state - État du module
     * @returns {string} Nom de la marque
     */
    brandName: (state) => state.branding.name,

    /**
     * Récupère la couleur de la barre de menu
     * @param {object} state - État du module
     * @returns {string} Code couleur hexadécimal
     */
    menuBarColor: (state) => state.branding.menuBarColor || '#2b303a',

    /**
     * Indique si le logo SVG doit être utilisé
     * @param {object} state - État du module
     * @returns {boolean} true si SVG, false si PNG
     */
    useSvgLogo: (state) => state.branding.useSvgLogo || false
  },

  mutations: {
    /**
     * Définit la configuration de branding
     * @param {object} state - État du module
     * @param {object} branding - Configuration branding
     */
    SET_BRANDING(state, branding) {
      state.branding = branding
    },

    /**
     * Définit le mode Light/Full
     * @param {object} state - État du module
     * @param {boolean} isLightMode - Mode Light (true) ou Full (false)
     */
    SET_LIGHT_MODE(state, isLightMode) {
      state.isLightMode = isLightMode
    }
  },

  actions: {
    /**
     * Initialise le branding selon le hostname
     * @param {object} context - Contexte Vuex
     * @param {string} hostname - Hostname actuel (window.location.hostname)
     */
    initBranding({ commit }, hostname) {
      const branding = getBrandingByHostname(hostname)
      commit('SET_BRANDING', branding)
      commit('SET_LIGHT_MODE', branding.isLightMode)
    }
  }
}
